import { api, bytesText, download, sessionJwt } from '../console/api.ts';
import { type CarrierView, carrierName } from '../console/carriers.ts';
import { empty, field, fileField, pageHead, pill } from '../console/components.ts';
import { ApiError, InputError } from '../console/errors.ts';
import { esc, icon, PRODUCT } from '../console/format.ts';
import { type Actions, current, render } from '../console/router.ts';
import { buildsCourses, has } from '../console/session.ts';
import { $, act, textInput } from '../console/ui.ts';
import {
  checkboxControl,
  checkedValues,
  numberControl,
  selectControl,
  textareaControl,
  textControl,
} from '../controls.ts';

/**
 * «Cursos»: the carriers' courses by modules and lessons in order, with their materials (PDF,
 * videos, reading guides) and the exam's question bank. The direction approves each course; an
 * approved course no longer changes. Onboarding shows the candidate's course with these pieces.
 */

export type MaterialView =
  | {
      id: string;
      kind: 'DOCUMENT';
      title: string;
      fileName: string;
      contentType: string;
      size: number;
    }
  | { id: string; kind: 'VIDEO'; title: string; url: string }
  | { id: string; kind: 'READING'; title: string; text: string };
export type LessonView = { id: string; title: string; materials: MaterialView[] };
export type CourseLessonView = LessonView & { status: 'DONE' | 'AVAILABLE' | 'LOCKED' };
export type ProgramView = {
  id: string;
  carrier: string;
  category: string | null;
  productLines: string[];
  title: string;
  modules: {
    id: string;
    title: string;
    summary: string;
    minutes: number;
    lessons: LessonView[];
  }[];
  exam: {
    passMark: number;
    maxAttempts: number;
    /** The question bank's size and how many questions each attempt draws from it. */
    bankSize: number;
    questionsPerAttempt: number;
    timeLimitMinutes: number | null;
    /** The bank's questions without answers (only in the course editor's views). */
    questions?: { id: string; text: string; options: string[] }[];
  };
  approved: { by: string; at: string } | null;
};
/** A graded attempt; those graded before the question bank carry only the score. */
export type ExamAttemptView = {
  at: string;
  score: number;
  passed: boolean;
  questions?: number;
  answered?: number;
  right?: number;
};
export type EnrollmentView = {
  id: string;
  program: ProgramView;
  modulesDone: string[];
  lessonsDone: string[];
  course: {
    id: string;
    title: string;
    summary: string;
    minutes: number;
    done: boolean;
    lessons: CourseLessonView[];
  }[];
  attempts: ExamAttemptView[];
  attemptsLeft: number;
  certificate: { id: string; issuedAt: string; program: string } | null;
  /** The attempt in progress: drawn questions (options as shown), saved answers, deadline. */
  exam: {
    startedAt: string;
    deadline: string | null;
    questions: { id: string; text: string; options: string[] }[];
    answers: Record<string, number>;
  } | null;
};
const CATEGORY: Record<string, string> = {
  AUTO: 'Autos',
  LIFE: 'Vida',
  SURETY: 'Fianzas',
  GENERAL: 'Ramos generales',
};
const LESSON_STATUS: Record<CourseLessonView['status'], [string, string]> = {
  DONE: ['ok', 'Completada'],
  AVAILABLE: ['info', 'Disponible'],
  LOCKED: ['CANCELLED', 'Bloqueada'],
};
/** The course whose editor is open, and the lesson whose material form is open. */
let courseOpen: string | null = null;

/** One material: a file to download, a video to open in a new tab, or a reading guide. */
export function materialHtml(
  m: MaterialView,
  download: (m: Extract<MaterialView, { kind: 'DOCUMENT' }>) => string,
) {
  if (m.kind === 'DOCUMENT')
    return `<li class="material">${icon('file')}<div><span class="small muted">${m.contentType === 'application/pdf' ? 'PDF' : 'Imagen'} · ${bytesText(m.size)}</span>${download(m)}${m.title !== m.fileName ? `<span class="small">${esc(m.title)}</span>` : ''}</div></li>`;
  if (m.kind === 'VIDEO')
    return `<li class="material">${icon('play')}<div><span class="small muted">Video</span><a href="${esc(m.url)}" target="_blank" rel="noopener noreferrer">${esc(m.title)}</a></div></li>`;
  return `<li class="material">${icon('book')}<div><span class="small muted">Guía de lectura</span><details><summary>${esc(m.title)}</summary><p class="reading">${esc(m.text)}</p></details></div></li>`;
}

/** A module of the candidate's course: lessons in order with their state and materials. */
export function courseModuleHtml(
  m: EnrollmentView['course'][number],
  a: {
    complete: (l: CourseLessonView) => string;
    download: (m: Extract<MaterialView, { kind: 'DOCUMENT' }>) => string;
  },
) {
  return `<div class="course-module ${m.done ? 'done' : ''}"><div class="course-module-head">${m.done ? icon('ok') : ''}<strong>${esc(m.title)}</strong><span class="muted small">${m.lessons.filter((l) => l.status === 'DONE').length} de ${m.lessons.length} lecciones</span></div>
    <ol class="lessons">${m.lessons
      .map((l) => {
        const [cls, label] = LESSON_STATUS[l.status];
        return `<li class="lesson ${l.status.toLowerCase()}"><div class="lesson-head">${l.status === 'LOCKED' ? icon('lock') : l.status === 'DONE' ? icon('ok') : ''}<strong>${esc(l.title)}</strong>${pill(cls, label)}<span class="spacer"></span>${l.status === 'AVAILABLE' ? a.complete(l) : ''}</div>
        ${l.status === 'LOCKED' ? '<p class="muted small">Se desbloquea al completar la lección anterior.</p>' : l.materials.length ? `<ul class="materials">${l.materials.map((x) => materialHtml(x, a.download)).join('')}</ul>` : ''}</li>`;
      })
      .join('')}</ol></div>`;
}

export async function cursos() {
  if (!buildsCourses())
    return (
      pageHead('Cursos', '') +
      empty(
        'lock',
        'Tu rol no arma cursos',
        'La dirección, la administración y la formación arman los cursos; la dirección los aprueba.',
        '',
        2,
      )
    );
  const [programs, carriers] = await Promise.all([
    api<ProgramView[]>('GET', '/v1/training-programs'),
    api<(CarrierView & { productLines: string[] })[]>('GET', '/v1/carriers'),
  ]);
  const lessons = (p: ProgramView) => p.modules.reduce((n, m) => n + m.lessons.length, 0);
  const drafts = programs.filter((p) => !p.approved);
  let html = pageHead(
    'Cursos',
    'Los cursos de las aseguradoras por módulos y lecciones en orden, con PDF, videos y guías de lectura. La postulante avanza lección por lección; la dirección aprueba cada curso y aprobado ya no cambia.',
  );
  html += `<section class="kpis" aria-label="Cursos">
    <div class="card kpi"><span class="kpi-label">${icon('cap')}Cursos</span><span class="kpi-value">${programs.length}</span><span class="kpi-foot">${programs.length - drafts.length} aprobados</span></div>
    <div class="card kpi"><span class="kpi-label">${icon('warn')}En borrador</span><span class="kpi-value">${drafts.length}</span><span class="kpi-foot">se editan hasta aprobarlos</span></div>
    <div class="card kpi"><span class="kpi-label">${icon('book')}Lecciones</span><span class="kpi-value">${programs.reduce((n, p) => n + lessons(p), 0)}</span><span class="kpi-foot">en todos los cursos</span></div>
  </section>`;
  const lines = [...new Set(carriers.flatMap((c) => c.productLines))];
  html += `<form id="course-form" class="card" novalidate><div class="card-head"><h2>Nuevo curso</h2></div>
    <div class="form-grid">
      <fieldset><legend>Curso</legend>
        ${field('Título', textControl({ id: 'course-title', maxlength: 120, placeholder: 'Vida 2026' }))}
        ${field(
          'Categoría',
          selectControl(
            { id: 'course-category' },
            Object.entries(CATEGORY).map(([k, v]) => ({ value: k, label: v })),
          ),
        )}
        ${field(
          'Aseguradora',
          selectControl(
            { id: 'course-carrier' },
            carriers.map((c) => ({ value: c.id, label: c.name })),
          ),
        )}
      </fieldset>
      <fieldset><legend>Para qué habilita</legend>
        <div class="field"><span>Líneas de producto</span><div id="course-lines" class="product-checks">${lines
          .map((l) => checkboxControl({ class: 'check', value: l }, esc(PRODUCT[l] ?? l)))
          .join('')}</div></div>
        ${field('Nota mínima del examen', numberControl({ id: 'course-pass', min: 1, max: 100, value: 80 }))}
        ${field('Intentos', numberControl({ id: 'course-attempts', min: 1, max: 10, value: 3 }))}
      </fieldset>
      <fieldset><legend>Módulos</legend>
        ${field('Un módulo por línea, en orden', textareaControl({ id: 'course-modules', rows: 4, placeholder: 'Coberturas y exclusiones\nSuscripción\nÉtica y cumplimiento' }))}
      </fieldset>
    </div>
    <div class="form-foot"><span class="muted small">Después agregas las lecciones de cada módulo, sus materiales y las preguntas del examen.</span>
      <button class="btn primary" type="submit" id="course-submit">Crear curso</button></div></form>`;
  if (!programs.length)
    return (
      html +
      empty('cap', 'No hay cursos todavía', 'Crea el primero: sus módulos, lecciones y materiales.')
    );
  const cards = programs
    .map((p) => {
      const draft = !p.approved;
      const open = courseOpen === p.id;
      const download = (m: Extract<MaterialView, { kind: 'DOCUMENT' }>) =>
        `<button type="button" class="link" data-course-material="${esc(p.id)}" data-material-id="${esc(m.id)}">${esc(m.fileName)}</button>`;
      const modules = p.modules
        .map(
          (
            m,
            i,
          ) => `<div class="course-module"><div class="course-module-head"><strong>${i + 1}. ${esc(m.title)}</strong><span class="muted small">${m.lessons.length} ${m.lessons.length === 1 ? 'lección' : 'lecciones'}${m.minutes ? ` · ${m.minutes} min` : ''}</span></div>
          <ol class="lessons">${m.lessons
            .map(
              (
                l,
              ) => `<li class="lesson"><div class="lesson-head"><strong>${esc(l.title)}</strong><span class="muted small">${l.materials.length} ${l.materials.length === 1 ? 'material' : 'materiales'}</span></div>
              ${l.materials.length ? `<ul class="materials">${l.materials.map((x) => materialHtml(x, download)).join('')}</ul>` : ''}
              ${
                draft
                  ? `<details class="add-material"><summary>Agregar material</summary><div class="step-form" data-material-form="${esc(l.id)}">
                  ${field(
                    'Tipo',
                    selectControl({ 'data-mat-kind': true }, [
                      { value: 'DOCUMENT', label: 'PDF u otro adjunto' },
                      { value: 'VIDEO', label: 'Enlace a video' },
                      { value: 'READING', label: 'Guía de lectura' },
                    ]),
                  )}
                  ${field('Título', textControl({ 'data-mat-title': true, maxlength: 120, placeholder: 'Condiciones generales' }))}
                  <div data-mat-for="DOCUMENT">${fileField('Archivo (PDF, JPG, PNG o WebP; hasta 10 MB)', { id: `mat-file-${l.id}`, accept: 'application/pdf,image/jpeg,image/png,image/webp', attrs: 'data-mat-file' })}</div>
                  <div data-mat-for="VIDEO" hidden>${field('Enlace del video (https://…)', textControl({ 'data-mat-url': true, type: 'url', placeholder: 'https://videos.example/vida' }))}</div>
                  <div data-mat-for="READING" hidden>${field('Guía de lectura', textareaControl({ 'data-mat-text': true, rows: 3, maxlength: 5000, placeholder: 'Lee el capítulo 2 de las condiciones generales.' }))}</div>
                  <div class="order-buttons"><button type="button" class="btn sm primary" data-add-material="${esc(p.id)}" data-lesson-id="${esc(l.id)}">Agregar material</button></div></div></details>`
                  : ''
              }</li>`,
            )
            .join('')}</ol>
          ${draft ? `<div class="toolbar order-actions" data-lesson-form>${field('Nueva lección', textControl({ 'data-lesson-title': m.id, maxlength: 120, placeholder: 'Coberturas' }))}<button type="button" class="btn sm" data-add-lesson="${esc(p.id)}" data-module-id="${esc(m.id)}">Agregar lección</button></div>` : ''}</div>`,
        )
        .join('');
      const bank = p.exam.questions ?? [];
      const questions = `<div class="course-module"><div class="course-module-head"><strong>Examen final</strong><span class="muted small">${bank.length} preguntas · nota mínima ${p.exam.passMark} · ${p.exam.maxAttempts} intentos</span></div>
        ${bank.length ? `<ol class="questions">${bank.map((q) => `<li>${esc(q.text)} <span class="muted small">(${q.options.map(esc).join(' · ')})</span></li>`).join('')}</ol>` : '<p class="muted small">Todavía no tiene preguntas: agrega al menos una para aprobar el curso.</p>'}
        ${
          draft
            ? `<details class="add-material"><summary>Agregar pregunta</summary><div class="step-form" data-question-form="${esc(p.id)}">
          ${field('Pregunta', textControl({ 'data-q-text': true, maxlength: 500, placeholder: '¿Quién recibe la suma asegurada?' }))}
          ${field('Opciones, una por línea', textareaControl({ 'data-q-options': true, rows: 3, placeholder: 'El contratante\nLos beneficiarios\nLa agencia' }))}
          ${field('Número de la opción correcta', numberControl({ 'data-q-answer': true, min: 1, max: 6, value: 1 }))}
          <div class="order-buttons"><button type="button" class="btn sm primary" data-add-question="${esc(p.id)}">Agregar pregunta</button></div></div></details>`
            : ''
        }</div>`;
      return `<article class="card course"><div class="card-head">
        <div class="policy-title"><strong>${esc(p.title)}</strong>
          <div class="policy-meta"><span>${esc(carrierName(p.carrier))}</span>${p.category ? `<span>${esc(CATEGORY[p.category] ?? p.category)}</span>` : ''}<span>${p.productLines.map((l) => esc(PRODUCT[l] ?? l)).join(', ') || 'Sin líneas de producto'}</span><span>${p.modules.length} módulos · ${lessons(p)} lecciones</span></div></div>
        <span class="spacer"></span>${draft ? pill('warn', 'Borrador') : pill('ok', 'Aprobado')}
        ${draft && has('agency_director') && bank.length ? `<button type="button" class="btn sm primary" data-approve-program="${esc(p.id)}">Aprobar</button>` : ''}</div>
        <details ${open ? 'open' : ''} data-course-details="${esc(p.id)}"><summary class="muted small">${draft ? 'Módulos, lecciones y materiales (editable)' : 'Módulos, lecciones y materiales'}</summary>
          <div class="course-body">${modules}${questions}</div></details></article>`;
    })
    .join('');
  return `${html}<section class="policies">${cards}</section>`;
}

/** The course editor closes when the session changes. */
export function resetCourses() {
  courseOpen = null;
}

export const actions: Actions = {
  click: {
    addLesson: (t, d) => {
      if (!d.addLesson || !d.moduleId) return;
      const programId = d.addLesson;
      // Module ids repeat across courses (M1, M2…): the field is the one next to this button.
      const input = t
        .closest('[data-lesson-form]')
        ?.querySelector<HTMLInputElement>('[data-lesson-title]');
      void act(t, 'Agregando la lección…', async () => {
        const title = input?.value.trim() ?? '';
        if (!title) throw new InputError('Escribe el título de la lección.');
        await api(
          'POST',
          `/v1/training-programs/${encodeURIComponent(programId)}/modules/${encodeURIComponent(d.moduleId ?? '')}/lessons`,
          { title },
        );
        courseOpen = programId;
        await render(current, { quiet: true });
        return `Lección «${title}» agregada.`;
      });
    },
    addMaterial: (t, d) => {
      if (!d.addMaterial || !d.lessonId) return;
      const programId = d.addMaterial;
      const lessonId = d.lessonId;
      const form = t.closest<HTMLElement>('[data-material-form]');
      const val = (sel: string) =>
        form?.querySelector<HTMLInputElement | HTMLTextAreaElement>(sel)?.value.trim() ?? '';
      void act(t, 'Agregando el material…', async () => {
        const kind = val('[data-mat-kind]');
        const title = val('[data-mat-title]');
        const path = `/v1/training-programs/${encodeURIComponent(programId)}/lessons/${encodeURIComponent(lessonId)}`;
        if (kind === 'DOCUMENT') {
          const file = form?.querySelector<HTMLInputElement>('[data-mat-file]')?.files?.[0];
          if (!file) throw new InputError('Elige el archivo (PDF o imagen).');
          if (file.size > 10 * 1024 * 1024)
            throw new InputError(
              `«${file.name}» pesa ${bytesText(file.size)}; el máximo es 10 MB.`,
            );
          const jwt = sessionJwt();
          const res = await fetch(`${path}/files`, {
            method: 'POST',
            headers: {
              ...(jwt ? { authorization: `Bearer ${jwt}` } : {}),
              'content-type': file.type || 'application/octet-stream',
              'x-file-name': encodeURIComponent(file.name),
              ...(title ? { 'x-material-title': encodeURIComponent(title) } : {}),
              'idempotency-key': crypto.randomUUID(),
            },
            body: file,
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok)
            throw new ApiError(res.status, data.code ?? String(res.status), data.message ?? '');
        } else if (kind === 'VIDEO') {
          if (!title) throw new InputError('Escribe el título del video.');
          await api('POST', `${path}/materials`, { kind, title, url: val('[data-mat-url]') });
        } else {
          if (!title) throw new InputError('Escribe el título de la guía.');
          const text = val('[data-mat-text]');
          if (!text) throw new InputError('Escribe la guía de lectura.');
          await api('POST', `${path}/materials`, { kind, title, text });
        }
        courseOpen = programId;
        await render(current, { quiet: true });
        return 'Material agregado a la lección.';
      });
    },
    addQuestion: (t, d) => {
      if (!d.addQuestion) return;
      const programId = d.addQuestion;
      const form = t.closest<HTMLElement>('[data-question-form]');
      void act(t, 'Agregando la pregunta…', async () => {
        const text = form?.querySelector<HTMLInputElement>('[data-q-text]')?.value.trim() ?? '';
        const options = (form?.querySelector<HTMLTextAreaElement>('[data-q-options]')?.value ?? '')
          .split('\n')
          .map((o) => o.trim())
          .filter(Boolean);
        const answer = Number(form?.querySelector<HTMLInputElement>('[data-q-answer]')?.value) - 1;
        if (!text) throw new InputError('Escribe la pregunta.');
        if (options.length < 2) throw new InputError('Escribe al menos dos opciones.');
        if (!(answer >= 0 && answer < options.length))
          throw new InputError(`La opción correcta es un número del 1 al ${options.length}.`);
        await api('POST', `/v1/training-programs/${encodeURIComponent(programId)}/questions`, {
          text,
          options,
          answer,
        });
        courseOpen = programId;
        await render(current, { quiet: true });
        return 'Pregunta agregada al examen.';
      });
    },
    courseMaterial: (t, d) => {
      if (!d.courseMaterial || !d.materialId) return;
      const path = `/v1/training-programs/${encodeURIComponent(d.courseMaterial)}/materials/${encodeURIComponent(d.materialId)}/content`;
      void act(t, 'Descargando…', async () => `«${await download(path)}» descargado.`);
    },
    // The same button in «Cursos» and in Onboarding's «Programas de capacitación».
    approveProgram: (t, d) => {
      if (!d.approveProgram) return;
      void act(t, 'Aprobando el programa…', async () => {
        const p = await api<ProgramView>(
          'POST',
          `/v1/training-programs/${encodeURIComponent(d.approveProgram ?? '')}/approve`,
          {},
        );
        await render(current, { quiet: true });
        return `Programa «${p.title}» aprobado.`;
      });
    },
  },
  submit: {
    'course-form': (ev) => {
      ev.preventDefault();
      void act($('#course-submit'), 'Creando el curso…', async () => {
        const title = textInput('#course-title', 'el título del curso');
        const modules = ($('#course-modules') as HTMLTextAreaElement).value
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean)
          .map((t) => ({ title: t }));
        if (!modules.length) throw new InputError('Escribe al menos un módulo.');
        const p = await api<ProgramView>('POST', '/v1/training-programs', {
          title,
          category: ($('#course-category') as HTMLSelectElement).value,
          carrier: ($('#course-carrier') as HTMLSelectElement).value,
          productLines: checkedValues(document, '#course-lines wa-checkbox'),
          modules,
          passMark: Number(($('#course-pass') as HTMLInputElement).value),
          maxAttempts: Number(($('#course-attempts') as HTMLInputElement).value),
        });
        courseOpen = p.id;
        await render(current, { quiet: true });
        return `Curso «${p.title}» creado con ${p.modules.length} módulos. Agrega sus lecciones.`;
      });
    },
  },
  changeData: {
    // Only the input of the chosen kind of material shows.
    matKind: (sel) => {
      for (const box of sel
        .closest('[data-material-form]')
        ?.querySelectorAll<HTMLElement>('[data-mat-for]') ?? [])
        box.hidden = box.dataset.matFor !== sel.value;
    },
  },
};
